import * as React from 'react';
import { cn, enumKeys } from '@/lib/utils';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Check, ChevronsUpDown } from 'lucide-react';
import { HELP_CENTER_LINKS } from '@/lib/onboardingTours/helpCenterLinks';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { FieldValues, useForm } from 'react-hook-form';
import { useIntegrations } from '@/hooks/integrations';
import { useEffect, useMemo, useState } from 'react';
import {
  DeviceType,
  IntegrationLimitedDto,
  IntegrationScriptLogDto,
} from '@/lib/integrationTypes/integrationTypes';
import { PageTitle } from '@/components/PageTitle';
import { toast } from '@/hooks/use-toast';
import ConnectionForm from './ConnectionForm';
import { useCreateExternalConnection, useTestExternalConnection } from '@/hooks/externalConnectionsApi';
import { useAuthStore } from '@/store/authStore';
import { connectionTypeName, ExternalConnectionType } from '@/lib/integrationTypes/externalConnectionTypes';
import ConnectionData from './ConnectionDataPreview';
import ConnectionDataPreview from './ConnectionDataPreview';
import { queryClient } from '@/lib/queryClient';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Textarea } from '@/components/ui/textarea';


const detailsFormSchema = z.object({
  name: z.string().min(2, {
    message: "Connection Name must be at least 2 characters.",
  }),
  description: z.string().optional(),
})


const AddDevice = () => {
  const [open, setOpen] = React.useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const connectionType = useMemo(() => {
    let candidate = Number.parseInt(searchParams.get('connectionType')?.toString() || '1');

    // if (isNaN(candidate) || candidate <= 0 || candidate >= enumKeys(ExternalConnectionType).length) {
    //   candidate = 1;
    // }

    return (candidate || 1) as ExternalConnectionType;
  }, [searchParams])

  const connectionTypeDisplay = useMemo(() => (
    connectionTypeName[connectionType]
  ), [connectionType])

  const navigate = useNavigate();

  const authStore = useAuthStore();


  const deviceType = useMemo<DeviceType>(() => (
    DeviceType[
    ExternalConnectionType[connectionType] as keyof typeof DeviceType
    ]
  ), [connectionType]);
  const [selectedIntegrationGuid, setSelectedIntegrationGuid] = React.useState('');


  const { data: integrationList, error } = useIntegrations();

  const {
    data: testConnectionResponse,
    error: connectionError,
    isPending: isConnectionPending,
    mutateAsync: testConnection,
    submittedAt: testConnectionSubmittedAt,

  } = useTestExternalConnection();

  const {
    mutateAsync: createConnection,
    isPending: isCreatingConnection,
    error: createConnectionError,
  } = useCreateExternalConnection({ queryClient: queryClient });


  const [form, setForm] = useState<FieldValues | null>(null);

  if (error) {
    console.error('Error fetching dashboard data:', error);
  }

  const possibleIntegrations =
    integrationList?.integrations.map((integration) => ({
      label: integration.name,
      value: integration.id,
      deviceType: DeviceType[integration.integrationType] as unknown as number,
    })) || [];

  const [selectedIntegration, setSelectedIntegration] = useState<IntegrationLimitedDto | undefined>(
    undefined
  );
  const detailsForm = useForm<z.infer<typeof detailsFormSchema>>({
    resolver: zodResolver(detailsFormSchema),
    defaultValues: {
      name: '',
      description: '',
    },
  });
  const { formState: { isValid: detailsFormValid } } = detailsForm;

  useEffect(() => {
    const integration = integrationList?.integrations.find((i) => i.id == selectedIntegrationGuid);
    setSelectedIntegration(integration);

    if (!detailsForm.getFieldState("name").isDirty) {
      detailsForm.setValue('name', integration?.name ?? "");
      detailsForm.trigger('name');
    }
  }, [selectedIntegrationGuid, integrationList, detailsForm]);

  const statusColors = {
    ready: 'bg-green-400',
    incomplete: 'bg-yellow-400',
    error: 'bg-red-400',
  };

  const headerLookup = {
    ready: 'Ready to publish',
    incomplete: 'Form Incomplete.',
    error: 'Errors Detected.',
  };

  interface NewDeviceStatus {
    status: 'ready' | 'incomplete' | 'error';
    message: string;
  }

  const [newDeviceStatus, setNewDeviceStatus] = useState<NewDeviceStatus>({
    status: 'incomplete',
    message: 'Please fill out all fields to continue.',
  });

  useEffect(() => {
    if (connectionError) {
      setNewDeviceStatus({
        status: 'error',
        message: testConnectionResponse ?? connectionError.message,
      });
      return;
    }

    if (
      !(selectedIntegrationGuid) || !detailsFormValid) {
      setNewDeviceStatus({
        status: 'incomplete',
        message: 'Please fill out all fields to continue.',
      });
      return;
    }

    if (
      !(testConnectionResponse)) {
      setNewDeviceStatus({
        status: 'incomplete',
        message: 'Please test the connection first.',
      });
      return;
    }



    setNewDeviceStatus({
      status: 'ready',
      message: 'All fields are filled out. Ready to publish.',
    });
  }, [
    selectedIntegrationGuid,
    connectionError,
    testConnectionResponse,
    detailsFormValid,
  ]);



  async function startAddDevice() {
    console.log('Full submit...');
    // if (!form) {
    //   console.error('No form data');
    //   toast({
    //     title: 'Failed to start monitoring',
    //     description: 'Fill out Integration Variables',
    //     variant: 'destructive',
    //   });
    //   return;
    // }

    try {
      let result = await createConnection({
        integrationId: selectedIntegrationGuid,
        integrationFields: form || {

        },
        name: detailsForm.getValues('name'),
        description: detailsForm.getValues('description'),
        deviceCreationChoices: testConnectionResponse?.deviceCreationChoices,
        deviceCreationKeyValues: testConnectionResponse?.deviceCreationKeyValues,

      });
      console.log('Result:', result);

      let dbId = result?.externalConnection?.id;

      if (!dbId) {
        console.error("Bad result on creation");
        toast({
          title: 'Failed to create connection',
          description: 'An error occurred while creating the connection',
          variant: 'destructive',
        });
      } else {
        navigate(`/connections/${dbId}`);

      }

    } catch (e) {

      toast({
        title: 'Failed to create connection',
        description: `${e}`,
        variant: 'destructive',
      });
    }
  }

  async function submitIntegrationVariables(values: FieldValues) {
    if (!selectedIntegrationGuid) {
      console.error('No integration selected');
      return;
    }

    console.log('Submitted values:', values);
    await testConnection({
      integrationFields: values,
      integrationId: selectedIntegrationGuid,
    });

  }


  return (
    <div>
      <PageTitle title="Add Connection" />
      <div className="sticky top-14 flex items-center justify-between border-b bg-[#111] text-white">
        <div className="flex items-center w-max">
          <Link to="/connections" className="border-r p-4 flex-shrink-0">
            <img src="/icon/chevron-left.svg" className="h-6 w-6" />
          </Link>
          <h1 className="mx-4 font-semibold justify-center">Connect</h1>
          <Select onValueChange={(value) => {
            const key = ExternalConnectionType[value as keyof typeof ExternalConnectionType];
            if (connectionType == key) return;

            setSearchParams({ connectionType: `${key}` });
            navigate(0);


          }} defaultValue={ExternalConnectionType[connectionType]}>
            <SelectTrigger >
              <SelectValue placeholder={connectionTypeDisplay} />
            </SelectTrigger>
            <SelectContent >
              <SelectGroup>
                {enumKeys(ExternalConnectionType).map((key) => (
                  <SelectItem
                    key={key}
                    value={key}
                    className='cursor-pointer'

                  >
                    {connectionTypeName[ExternalConnectionType[key as keyof typeof ExternalConnectionType]]}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          <p className="text-sm text-gray-500 ml-4" title="Current Tenant">{authStore?.getTenantData()?.tenantName || authStore?.getTenantData()?.tenantId || "Unknown Tenant"}</p>
        </div>
      </div>
      <section className="mx-auto my-16 w-full text-white">
        <div className="mx-auto mt-8 w-full max-w-[1200px]">
          <div className="mt-6">
            <section className="mt-6 rounded-xl border bg-[#111]">
              <div className="p-6">
                <h2 className="text-xl font-semibold">Connect an {connectionTypeDisplay} to Watchful OS</h2>

                <div className="mt-4 grid flex-1 items-center gap-1.5">
                  <Label htmlFor="deviceModel"></Label>
                  <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className="w-full justify-between text-white"
                      >
                        {selectedIntegrationGuid
                          ? possibleIntegrations.find(
                            (framework) => framework.value === selectedIntegrationGuid
                          )?.label
                          : `Select ${connectionTypeDisplay} Integration...`}
                        <ChevronsUpDown className="text-lime" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search integrations..." className="h-9" />
                        <CommandList>
                          <CommandEmpty>No Integrations found.</CommandEmpty>
                          <CommandGroup>
                            {possibleIntegrations
                              .filter((framework) => framework.deviceType == deviceType)
                              .map((framework) => (
                                <CommandItem
                                  key={framework.value}
                                  value={framework.value}
                                  keywords={[framework.label]}
                                  className='cursor-pointer'
                                  onSelect={(currentValue) => {
                                    setSelectedIntegrationGuid(
                                      currentValue === selectedIntegrationGuid ? '' : currentValue
                                    );
                                    setOpen(false);
                                  }}
                                >
                                  {framework.label}
                                  <Check
                                    className={cn(
                                      'ml-auto',
                                      selectedIntegrationGuid === framework.value
                                        ? 'opacity-100'
                                        : 'opacity-0'
                                    )}
                                  />
                                </CommandItem>
                              ))}
                          </CommandGroup>
                        </CommandList>
                        <Link
                          target='_blank'
                          to={HELP_CENTER_LINKS.contactUs}
                          className={`rounded-none border-b-0 border-l-0 border-r-0 ${buttonVariants({ variant: 'outline' })}`}
                        >
                          Request a new integration
                        </Link>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </section>
            {selectedIntegration ? (<section className="mt-6 rounded-xl border bg-[#111]">
              <h2 className="border-b p-6 text-xl font-semibold">About this Device</h2>
              <div className="p-6">
                <Form {...detailsForm}>
                  <div className="mt-4 flex flex-wrap gap-4">
                    <FormField
                      control={detailsForm.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem className="grid flex-1 items-center gap-1.5">
                          <FormLabel>Connection Name</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter device name"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="mt-4 flex flex-wrap gap-4"></div>

                  <div className="mt-4">
                    <div className="grid w-full gap-1.5">
                      <FormField
                        control={detailsForm.control}
                        name="description"
                        render={({ field }) => (
                          <FormItem className="grid flex-1 items-center gap-1.5">
                            <FormLabel>Description</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="Enter device description or notes"
                                className="min-h-[120px]"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />

                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </Form>
              </div>
            </section>
            ) : <></>}
            {selectedIntegration ? (<>
              <ConnectionForm
                integration={selectedIntegration}
                onVariableSubmit={submitIntegrationVariables}
                onFormChange={setForm}
                currentlyProcessing={false}
                submitText='Test Connection'
                logs={testConnectionResponse?.scriptLog}
              />
              <ConnectionDataPreview loading={isConnectionPending} connectionData={testConnectionResponse} integration={selectedIntegration} dataDate={new Date(testConnectionSubmittedAt)} />
            </>
            ) : (
              <section className="mt-6 rounded-xl border bg-[#111]">
                <div className="p-6">
                  <h2 className="pb-6 text-xl font-semibold">Select an integration above.</h2>
                  <p className="text-gray-400">No device selected</p>
                </div>
              </section>
            )}
            <section className="mt-6 rounded-xl border bg-[#111] p-6">
              <div className="flex justify-between rounded-lg border p-4">
                <div className="flex items-center gap-4">
                  <div className={`h-4 w-4 rounded-full ${statusColors[newDeviceStatus.status]}`} />
                  <p>
                    <strong>{headerLookup[newDeviceStatus.status]}</strong>
                    <br />
                    {newDeviceStatus.message}
                  </p>
                </div>
                <div className="justify-self-end">
                  <Button
                    disabled={newDeviceStatus.status != 'ready'}
                    onClick={startAddDevice}
                  >
                    Save External Connection
                  </Button>
                </div>
              </div>
            </section>
          </div>
        </div>
      </section >
    </div >
  );
};

export default AddDevice;
